import type { CrossbrandItem } from '@integration-layer/ampliance/schemas/crossbrand-item-schema.localized'
import type { HomepageBrandCardWrapper } from '@design-system/components/Homepage/HomepageBrandCardWrapper.props'
import type { CrossbrandMobileCategory } from '@integration-layer/ampliance/schemas/crossbrand-mobile-category-schema.localized'
import type { CrossbrandMobileCategoryItem } from '@design-system/components/Homepage/HomepageMobileCategories.props.ts'

/**
 * Connects and transforms components with metadata for delivery.
 *
 * This function maps components to a standardized format with delivery metadata,
 * including locale, VSE (Versioned Staging Environment), and content.
 *
 * @param {Array<{ _meta: { name?: string; schema?: string } } & Record<string, any>>} components - The array of components to connect.
 * @param {string} locale - The locale for the components.
 * @param {string | undefined} vse - The Versioned Staging Environment identifier.
 * @returns {Array<{
 *   deliveryId: string,
 *   locale: string,
 *   vse: string,
 *   name: string,
 *   content?: Record<string, any>
 * }>} An array of connected components with delivery metadata.
 *
 * @example
 * // Connect components with locale and VSE
 * connectComponents(myComponents, 'en', 'staging-123')
 */
export const connectComponents = (
  components: Array<
    { _meta: { name?: string; schema?: string } } & Record<string, any>
  >,
  locale: string,
  vse: string | undefined
) => {
  return (
    components?.map(c => {
      const { _meta, ...content } = c
      return {
        // @ts-ignore
        deliveryId: _meta.deliveryId as unknown as string,
        locale,
        vse: vse ?? '',
        name: _meta.schema!,
        content: Object.keys(content).length ? content : undefined,
        userTags: c.userTags ?? undefined,
      }
    }) ?? []
  )
}

/**
 * Transforms a CrossbrandItem into a HomepageBrandCardWrapper.
 *
 * This function maps a crossbrand item to a standardized brand card format,
 * handling media, logos, and labels with fallback values.
 *
 * @param {CrossbrandItem} card - The crossbrand item to transform.
 * @returns {HomepageBrandCardWrapper} A transformed homepage brand card wrapper.
 *
 * @example
 * // Transform a crossbrand item to a brand card
 * const brandCard = connectCrossbrandCard(myCrossbrandItem)
 */
export const connectCrossbrandCard = (
  card: CrossbrandItem
): HomepageBrandCardWrapper => {
  return {
    card: {
      brand: card.brand,
      link: card.link,
      mainMedia: {
        type: card.mainMedia?.type,
        src: card.mainMedia?.url?.public_id ?? '',
        alt: card.mainMedia?.alt ?? '',
      },
      hoverMedia: {
        type: card?.hoverMedia?.type,
        src: card.hoverMedia?.url?.public_id ?? '',
        alt: card.hoverMedia?.alt as string,
      },
      bannerLogo: {
        type: card.bannerLogo?.type,
      },
      hoverLogo: card.hoverLogo?.[0]
        ? {
            type: card.hoverLogo[0].type,
            color: card.hoverLogo[0].color,
            overlayBackground: card.hoverLogo[0].overlayBackground,
          }
        : undefined,
      // @ts-ignore
      firstLabel: card.firstLabel,
      secondLabel: card.secondLabel ? card.secondLabel?.[0] : undefined,
    },
  }
}
/**
 * Transforms a CrossbrandItem into a HomepageBrandCardWrapper.
 *
 * This function maps a crossbrand item to a standardized brand card format,
 * handling media, logos, and labels with fallback values.
 *
 * @param {CrossbrandMobileCategory} category - The crossbrand item to transform.
 * @returns {HomepageBrandCardWrapper} A transformed homepage brand card wrapper.
 *
 * @example
 * // Transform a crossbrand item to a brand card
 * const brandCard = connectCrossbrandCard(myCrossbrandItem)
 */
export const connectCrossbrandMobileCategory = (
  category: CrossbrandMobileCategory
): CrossbrandMobileCategoryItem => {
  const items = category.items?.map(
    card => connectCrossbrandCard(card as unknown as CrossbrandItem).card
  )
  return {
    title: category.title || '',
    items: items || [],
  }
}
